/**
 * AIConfirmationsSettings - Settings for AI Tool Auto-Approve
 *
 * sl-canvas2-8.31: Allows users to configure which AI tools should
 * be auto-approved without confirmation.
 *
 * Tools are grouped by the canonical backend ToolCategory. Confirmation
 * eligibility comes from the effect contract, never from name prefixes.
 */
import { notify } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { WorkflowClientService } from '@affine/core/modules/streamlain/services/workflow-client';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getToggleState, MixedSwitch } from '../../shared/grouped-toggle-list';
import { SettingsOfflineHeaderAction } from '../../shared/settings-offline-status';
import { GroupedToolPolicyList } from '../../workspace-setting/mcp-access/mcp-review';
import { SettingsTabs } from '../../workspace-setting/shared/settings-navigation';
import {
  type AssistantPreference,
  type ConfirmationSurface,
  isToolDelegated,
  type PreferenceSelectorType,
  replaceSurfacePreference,
  selectorsForToolGroup,
} from './confirmation-preferences';

interface ToolDisplayName {
  de: string;
  en: string;
}

interface ConfirmationTool {
  actionCapability?: string;
  actionRisk?: string;
  allowedSurfaces?: string[];
  category: string;
  confirmationDefault: 'auto_when_allowed' | 'require_confirmation';
  description: string;
  displayName: ToolDisplayName;
  name: string;
}

interface ToolGroup {
  key: string;
  tools: ConfirmationTool[];
  requiresWorkflowEngine?: boolean;
}

function groupTools(tools: ConfirmationTool[]): ToolGroup[] {
  const byCategory = new Map<string, ConfirmationTool[]>();
  for (const tool of tools) {
    const category = tool.category || 'utility';
    byCategory.set(category, [...(byCategory.get(category) ?? []), tool]);
  }
  return [...byCategory.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, categoryTools]) => ({
      key,
      tools: categoryTools.sort((a, b) => a.name.localeCompare(b.name)),
      requiresWorkflowEngine: key === 'automation',
    }));
}

interface AIConfirmationsSettingsProps {
  showHeader?: boolean;
}

export const AIConfirmationsSettings = ({
  showHeader = true,
}: AIConfirmationsSettingsProps = {}) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const [tools, setTools] = useState<ConfirmationTool[]>([]);
  const [preferences, setPreferences] = useState<AssistantPreference[]>([]);
  const [activeSurface, setActiveSurface] =
    useState<ConfirmationSurface>('copilot');
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState<string | null>(null);
  const workflowClient = useService(WorkflowClientService);
  const workspace = useService(WorkspaceService).workspace;
  const isWorkflowEngineActive = useLiveData(workflowClient.isConfigured$);

  useEffect(() => {
    const fetchTools = async () => {
      if (!backendAvailable) {
        setIsLoading(false);
        return;
      }
      try {
        const [toolsResponse, settingsResponse] = await Promise.all([
          workspaceApiFetch(
            `/api/workspace/${workspace.id}/assistant/me/confirmation-tools`,
            { credentials: 'include' },
            {
              workspaceId: workspace.id,
              category: 'settings',
              mode: 'background',
              priority: 'visible',
            }
          ),
          workspaceApiFetch(
            `/api/workspace/${workspace.id}/assistant/me`,
            { credentials: 'include' },
            {
              workspaceId: workspace.id,
              category: 'settings',
              mode: 'background',
              priority: 'visible',
            }
          ),
        ]);
        if (toolsResponse.ok && settingsResponse.ok) {
          const toolsData = await toolsResponse.json();
          const settingsData = (await settingsResponse.json()) as {
            preferences?: AssistantPreference[];
          };
          setTools(toolsData.tools ?? []);
          setPreferences(settingsData.preferences ?? []);
        }
      } catch (error) {
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        console.error('Failed to fetch confirmation tools:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTools().catch(console.error);
  }, [backendAvailable, workspace.id]);

  const saveSelectorPreference = useCallback(
    async (
      surface: ConfirmationSurface,
      selectorType: PreferenceSelectorType,
      selector: string,
      enabled: boolean
    ) => {
      const response = await workspaceApiFetch(
        `/api/workspace/${workspace.id}/assistant/me/preferences`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            selectorType,
            selector,
            mode: enabled ? 'auto_when_allowed' : 'require_confirmation',
            surface,
          }),
        },
        {
          workspaceId: workspace.id,
          category: 'settings',
          mode: 'foreground',
          priority: 'foreground',
        }
      );
      if (!response.ok) throw new Error('Failed to update setting');
    },
    [workspace.id]
  );

  const visibleGroups = useMemo(() => {
    const surfaceTools = tools.filter(
      tool =>
        !tool.allowedSurfaces || tool.allowedSurfaces.includes(activeSurface)
    );
    const groups = groupTools(surfaceTools);
    return groups.filter(
      g => !g.requiresWorkflowEngine || isWorkflowEngineActive
    );
  }, [activeSurface, tools, isWorkflowEngineActive]);

  const visibleTools = useMemo(
    () => visibleGroups.flatMap(g => g.tools),
    [visibleGroups]
  );

  const saveSelectorBatch = useCallback(
    async (
      selectors: Array<{ type: PreferenceSelectorType; value: string }>,
      enabled: boolean
    ) => {
      const results = await Promise.allSettled(
        selectors.map(selector =>
          saveSelectorPreference(
            activeSurface,
            selector.type,
            selector.value,
            enabled
          )
        )
      );
      const savedSelectors = selectors.filter(
        (_, index) => results[index]?.status === 'fulfilled'
      );
      setPreferences(prev =>
        savedSelectors.reduce(
          (next, selector) =>
            replaceSurfacePreference(
              next,
              activeSurface,
              selector.type,
              selector.value,
              enabled ? 'auto_when_allowed' : 'require_confirmation'
            ),
          prev
        )
      );
      if (savedSelectors.length !== selectors.length) {
        throw new Error('Some confirmation settings failed to update');
      }
    },
    [activeSurface, saveSelectorPreference]
  );

  const handleToggle = useCallback(
    async (toolName: string, enabled: boolean) => {
      setIsMutating(toolName);
      try {
        const selected = tools.find(tool => tool.name === toolName);
        if (!selected) return;
        await saveSelectorPreference(activeSurface, 'tool', toolName, enabled);
        setPreferences(prev =>
          replaceSurfacePreference(
            prev,
            activeSurface,
            'tool',
            toolName,
            enabled ? 'auto_when_allowed' : 'require_confirmation'
          )
        );
      } catch {
        notify.error({
          title: t['com.streamlain.ai-confirmations.save-failed'](),
        });
      } finally {
        setIsMutating(null);
      }
    },
    [activeSurface, saveSelectorPreference, t, tools]
  );

  const handleToggleAll = useCallback(
    async (enabled: boolean) => {
      setIsMutating('all');
      try {
        await saveSelectorBatch(
          visibleGroups.flatMap(group =>
            selectorsForToolGroup(
              group.key,
              group.tools.map(tool => tool.name)
            )
          ),
          enabled
        );
      } catch {
        notify.error({
          title: t['com.streamlain.ai-confirmations.save-failed'](),
        });
      } finally {
        setIsMutating(null);
      }
    },
    [saveSelectorBatch, t, visibleGroups]
  );

  const handleToggleGroup = useCallback(
    async (group: ToolGroup, enabled: boolean) => {
      setIsMutating(`group:${group.key}`);
      try {
        await saveSelectorBatch(
          selectorsForToolGroup(
            group.key,
            group.tools.map(tool => tool.name)
          ),
          enabled
        );
      } catch {
        notify.error({
          title: t['com.streamlain.ai-confirmations.save-failed'](),
        });
      } finally {
        setIsMutating(null);
      }
    },
    [saveSelectorBatch, t]
  );

  const allEnabled =
    visibleTools.length > 0 &&
    visibleTools.every(tool =>
      isToolDelegated(preferences, activeSurface, tool)
    );
  const allState = getToggleState(visibleTools, tool =>
    isToolDelegated(preferences, activeSurface, tool)
  );
  const groupedPolicyTools = useMemo(
    () =>
      visibleGroups.map(group => ({
        category: group.key,
        tools: group.tools.map(tool => ({
          name: tool.name,
          category: tool.category,
          description: tool.description,
          requiresConfirmation: true,
          source: 'builtin' as const,
        })),
      })),
    [visibleGroups]
  );
  const nonDelegatedTools = useMemo(
    () =>
      new Set(
        visibleTools
          .filter(tool => !isToolDelegated(preferences, activeSurface, tool))
          .map(tool => tool.name)
      ),
    [activeSurface, preferences, visibleTools]
  );
  const loadingLabel = backendAvailable
    ? t['com.streamlain.ai-confirmations.loading']()
    : null;
  const emptyLabel = backendAvailable
    ? t['com.streamlain.ai-confirmations.empty']()
    : null;
  const offlineStatusLabel = backendAvailable
    ? null
    : t['com.streamlain.ai-confirmations.offline']();
  const renderStatusMessage = (label: string | null) =>
    label ? (
      <div
        style={{
          padding: '16px 0',
          color: 'var(--streamlain-text-secondary-color)',
        }}
      >
        {label}
      </div>
    ) : null;

  return (
    <>
      {showHeader ? (
        <SettingHeader
          title={t['com.streamlain.ai-confirmations.title']()}
          subtitle={t['com.streamlain.ai-confirmations.subtitle']()}
          action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
        />
      ) : null}

      <SettingsTabs
        ariaLabel={t['com.streamlain.ai-confirmations.surface-tabs-label']()}
        activeTab={activeSurface}
        tabs={[
          {
            id: 'copilot',
            label: t['com.streamlain.ai-confirmations.surface-copilot'](),
            testId: 'ai-confirmations-surface-copilot',
            disabled: isMutating !== null,
          },
          {
            id: 'mcp',
            label: t['com.streamlain.ai-confirmations.surface-mcp'](),
            testId: 'ai-confirmations-surface-mcp',
            disabled: isMutating !== null,
          },
        ]}
        onChange={surface => {
          if (surface === 'copilot' || surface === 'mcp') {
            setActiveSurface(surface);
          }
        }}
      />

      <div
        data-testid={`ai-confirmations-surface-description-${activeSurface}`}
        style={{
          padding: '4px 0 12px',
          color: 'var(--streamlain-text-secondary-color)',
          lineHeight: '20px',
        }}
      >
        {activeSurface === 'mcp'
          ? t['com.streamlain.ai-confirmations.surface-mcp-desc']()
          : t['com.streamlain.ai-confirmations.surface-copilot-desc']()}
      </div>

      <SettingWrapper title={t['com.streamlain.ai-confirmations.general']()}>
        {offlineStatusLabel && visibleTools.length === 0 ? (
          <SettingRow disabled name={offlineStatusLabel} spreadCol={false} />
        ) : !isLoading && visibleTools.length > 0 ? (
          <SettingRow
            name={
              activeSurface === 'mcp'
                ? t['com.streamlain.ai-confirmations.all-actions-mcp']()
                : t['com.streamlain.ai-confirmations.all-actions']()
            }
            desc={
              activeSurface === 'mcp'
                ? t['com.streamlain.ai-confirmations.all-actions-mcp-desc']()
                : t['com.streamlain.ai-confirmations.all-actions-desc']()
            }
          >
            <MixedSwitch
              testId="ai-auto-approve-all"
              state={allState}
              disabled={!backendAvailable || isMutating !== null}
              onToggle={() => void handleToggleAll(!allEnabled)}
            />
          </SettingRow>
        ) : null}
      </SettingWrapper>

      {isLoading ? (
        renderStatusMessage(loadingLabel)
      ) : visibleTools.length === 0 ? (
        renderStatusMessage(emptyLabel)
      ) : (
        <GroupedToolPolicyList
          role="assistant"
          groups={groupedPolicyTools}
          loading={false}
          disabledTools={nonDelegatedTools}
          title={
            activeSurface === 'mcp'
              ? t['com.streamlain.ai-confirmations.surface-mcp']()
              : t['com.streamlain.ai-confirmations.surface-copilot']()
          }
          selectionNote={
            activeSurface === 'mcp'
              ? t['com.streamlain.ai-confirmations.surface-mcp-desc']()
              : t['com.streamlain.ai-confirmations.surface-copilot-desc']()
          }
          onToggleCategory={category => {
            const group = visibleGroups.find(item => item.key === category);
            if (!group || isMutating !== null) return;
            const state = getToggleState(group.tools, tool =>
              isToolDelegated(preferences, activeSurface, tool)
            );
            void handleToggleGroup(group, state !== 'on');
          }}
          onToggleTool={toolName => {
            const tool = visibleTools.find(item => item.name === toolName);
            if (!tool || isMutating !== null) return;
            void handleToggle(
              toolName,
              !isToolDelegated(preferences, activeSurface, tool)
            );
          }}
          testId="ai-confirmation-tool-policy"
        />
      )}

      {!isLoading && visibleTools.length > 0 ? (
        <div
          style={{
            paddingTop: 12,
            fontSize: 12,
            color: 'var(--streamlain-text-secondary-color)',
            lineHeight: '18px',
          }}
        >
          <strong>{t['com.streamlain.ai-confirmations.note-label']()}</strong>{' '}
          {activeSurface === 'mcp'
            ? t['com.streamlain.ai-confirmations.note-mcp']()
            : t['com.streamlain.ai-confirmations.note']()}
        </div>
      ) : null}
    </>
  );
};
